import { useMemo, useRef } from 'react';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import axiosInstance from '../../../constants/axiosInstance';
import { useIntersectionObserver } from '../../../utilities/customHooks/useIntersectionObserver';

interface SenderDetails {
  senderId: string;
  name: string;
}

interface InsightComment {
  senderDetails: SenderDetails;
  comment: string;
  sentAt: string;
}

interface InsightCommentsResponse {
  listOfInsightComments: InsightComment[];
  totalPages: number;
  totalCount: number;
}


const fetchComments = async ({
  pageParam = 1,
  queryKey,
}: {
  pageParam?: number;
  queryKey: (string | boolean)[];
  isPublic: boolean;
}): Promise<InsightCommentsResponse & { currentPage: number }> => {
  const [_key, insightId, isPublic] = queryKey as [string, string, boolean];
  const Url = isPublic ? '/deeplinks/getListOfInsightComments' : '/insights/getListOfInsightComments';
  const { data } = await axiosInstance().get(Url, {
    params: {
      insightId,
      pageNumber: pageParam,
      limit: 50,
    },
  });

  return {
    ...data.data,
    currentPage: pageParam,
  };
};

const getNextPageParam = (lastPage: InsightCommentsResponse & { currentPage: number }) => {
  const { currentPage, totalPages } = lastPage;
  return currentPage < totalPages ? currentPage + 1 : undefined;
};

export const useInsightComments = (insightId: string, isPublic: boolean) => {
  const lastElementRef = useRef<HTMLDivElement>(null);
  const commentContainerRef = useRef<HTMLDivElement>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery({
    queryKey: ['insightComments', insightId, isPublic],
    queryFn: ({ queryKey }) => fetchComments({ queryKey, isPublic }),
    getNextPageParam,
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const comments = useMemo(() => {
    return data.pages.flatMap(page => page.listOfInsightComments);
  }, [data]);

  useIntersectionObserver(
    commentContainerRef,
    lastElementRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  );

  return {
    comments,
    lastElementRef,
    commentContainerRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
